import React, { useState } from "react";
import { Inbox } from "lucide-react";
import PlansGrid from "./PlanGrid";
import PlanConfigModal from "./PlanConfigModal";
import usePlans from "../hooks/usePlans";
import { getPlanId } from "../utils/PlanUtils";

export const PLAN_TENANT_ID = "T1111001XT7";


const ExistingPlansSection = ({ onBuy, onBrowseBenefits, isActive = true }) => {
  const { plans, loading, error, refetch } = usePlans(PLAN_TENANT_ID);

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);

  const safePlans = Array.isArray(plans) ? plans.filter((b) => b?.status === "PUBLISHED" ): [];
  const activePlan = activeIndex !== null ? safePlans[activeIndex] ?? null : null;

  const handleSelectPlan = (plan) => {
    if (!plan) return;
    setSelectedPlan(plan);
    setDetailsOpen(true);
  };

  const handleCardClick = (plan, index) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  };

  const handleBuyActivePlan = () => {
    if (!activePlan) return;
    const totalMembers = Number(activePlan.members) || 0;
    const additionalMembers = Math.max(0, totalMembers - 1);
    onBuy({
      tenantId: PLAN_TENANT_ID,
      planCode:
        activePlan.planId ?? activePlan.code ?? activePlan.planName ?? activePlan.name,
      members: additionalMembers,
    });
  };

  const selectedId = selectedPlan ? getPlanId(selectedPlan) : null;

  return (
    <section aria-labelledby="existing-plans-heading">
      
      <div
  className="
    relative isolate overflow-hidden
    rounded-2xl
    border border-[#E9E1FA]
    bg-gradient-to-br from-[#FAF8FF] via-[#F7F3FF] to-[#F0E9FF]
    px-5 py-6
    sm:px-7 sm:py-7
    md:px-9 md:py-8
  "
>
  {/* =========================================================
      BACKGROUND DECORATION
  ========================================================== */}

  {/* Large soft purple glow */}
  <div
    className="
      pointer-events-none absolute
      -left-20 -top-28
      h-64 w-64
      rounded-full
      bg-purple-300/20
      blur-3xl
    "
  />

  {/* Blue glow */}
  <div
    className="
      pointer-events-none absolute
      -bottom-32 right-[25%]
      h-72 w-72
      rounded-full
      bg-blue-200/20
      blur-3xl
    "
  />

  {/* Organic curved SVG */}
  <svg
    className="
      pointer-events-none absolute
      right-0 top-0
      h-full
      w-[65%]
      opacity-70
    "
    viewBox="0 0 800 300"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio="none"
  >
    <path
      d="
        M800 0
        H480
        C590 35 610 95 570 145
        C530 195 565 250 690 300
        H800
        V0Z
      "
      fill="url(#purpleGradient)"
    />

    <path
      d="
        M800 0
        H590
        C680 45 700 90 670 130
        C635 180 665 240 750 300
        H800
        V0Z
      "
      fill="white"
      fillOpacity="0.32"
    />

    <defs>
      <linearGradient
        id="purpleGradient"
        x1="480"
        y1="0"
        x2="800"
        y2="300"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E9DEFF" stopOpacity="0.65" />
        <stop
          offset="1"
          stopColor="#D8C7FF"
          stopOpacity="0.25"
        />
      </linearGradient>
    </defs>
  </svg>

  {/* Decorative circles */}
  <div
    className="
      pointer-events-none absolute
      right-[30%] top-8
      h-3 w-3
      rounded-full
      bg-purple-400/40
    "
  />

  <div
    className="
      pointer-events-none absolute
      right-[26%] top-16
      h-2 w-2
      rounded-full
      bg-blue-400/40
    "
  />

  {/* =========================================================
      MAIN CONTENT
  ========================================================== */}

  <div
    className="
      relative z-10
      flex flex-col
      gap-7
      sm:flex-row
      sm:items-center
      sm:justify-between
      sm:gap-8
    "
  >
    {/* =======================================================
        LEFT CONTENT
    ======================================================== */}

    <div className="min-w-0 flex-1">
      {/* Eyebrow */}
      <div
        className="
          mb-3 inline-flex
          items-center gap-2
          rounded-full
          border border-purple-200/80
          bg-white/75
          px-3 py-1.5
          shadow-sm
          backdrop-blur-sm
        "
      >
        <span
          className="
            flex h-5 w-5
            items-center justify-center
            rounded-full
            bg-purple-100
            text-[11px]
            text-purple-700
          "
        >
          ✓
        </span>

        <span
          className="
            text-[10px]
            font-bold
            uppercase
            tracking-[0.12em]
            text-purple-700
            sm:text-xs
          "
        >
          Simple Healthcare Plans
        </span>
      </div>

      {/* Heading */}
      <h2
        id="customize-plan-heading"
        className="
          max-w-[560px]
          text-[28px]
          font-extrabold
          leading-[1.08]
          tracking-[-0.025em]
          text-[#14131A]
          sm:text-[32px]
          md:text-[36px]
          lg:text-[40px]
        "
      >
        Find a healthcare plan that's{" "}
        <span
          className="
            bg-gradient-to-r
            from-[#6D28D9]
            via-[#7C3AED]
            to-[#9333EA]
            bg-clip-text
            text-transparent
          "
        >
          designed for you
        </span>
      </h2>

      {/* Description */}
      <p
        className="
          mt-3
          max-w-[540px]
          text-sm
          leading-6
          text-[#5B5866]
          sm:text-base
        "
      >
       Explore thoughtfully curated healthcare plans with expert-backed benefits to support your everyday health and well-being.
      </p>

      {/* Benefits */}
      <div className="mt-5 flex flex-wrap gap-2">
        <div
          className="
            inline-flex items-center gap-2
            rounded-full
            bg-white/90
            px-3 py-2
            text-xs font-medium
            text-purple-800
            shadow-sm
            ring-1 ring-purple-200
          "
        >
          <span className="text-purple-800">✓</span>
          Pre-designed benefits
        </div>
        <div
          className="
            inline-flex items-center gap-2
            rounded-full
            bg-white/90
            px-3 py-2
            text-xs font-medium
            text-purple-800
            shadow-sm
            ring-1 ring-purple-200
          "
        >
          <span className="text-purple-800">✓</span>
          Expert curated 
        </div>
        <div
          className="
            inline-flex items-center gap-2
            rounded-full
            bg-white/90
            px-3 py-2
            text-xs font-medium
            text-purple-800
            shadow-sm
            ring-1 ring-purple-200
          "
        >
          <span className="text-purple-800">✓</span>
          Easy to choose
        </div>

        <div
          className="
            inline-flex items-center gap-2
            rounded-full
            bg-white/90
            px-3 py-2
            text-xs font-medium
            
            shadow-sm
            ring-2 ring-purple-900
            text-purple-800
          "
        >
          <span className="text-purple-800">✓</span>
          Better every day health
        </div>
      </div>
    </div>

    {/* =======================================================
        RIGHT ILLUSTRATION
    ======================================================== */}

    <div
      className="
        relative
        flex
        w-full
        shrink-0
        items-center
        justify-center
        sm:w-[34%]
        md:w-[32%]
        lg:w-[30%]
      "
    >
      {/* Organic image background */}
      <div
        className="
          absolute
          h-[190px] w-[190px]
          rounded-[42%_58%_55%_45%/48%_45%_55%_52%]
          bg-gradient-to-br
          from-[#E6DAFF]
          to-[#D9CBFA]
          shadow-inner
          sm:h-[210px] sm:w-[210px]
          md:h-[200px] md:w-[205px]
        "
      />

      {/* Secondary blob */}
      <div
        className="
          absolute
          -right-2 bottom-1
          h-16 w-16
          rounded-full
          bg-[#CFC0F5]/40
          blur-xl
        "
      />

      {/* Illustration */}
      <div className="relative z-10">
        <img
          src="/assets/images/banner-2.png"
          alt="Family choosing a healthcare plan"
          className="
            mx-auto
            h-auto
            w-[190px]
            object-contain
            drop-shadow-[0_12px_18px_rgba(80,50,130,0.14)]
            sm:w-[215px]
            md:w-[235px]
            lg:w-[250px]
          "
        />
      </div>

      {/* Floating badge - top */}
      <div
        className="
          absolute
          right-1 top-3
          z-20
          hidden
          items-center gap-1.5
          rounded-xl
          border border-white/70
          bg-white/90
          px-2.5 py-2
          text-[10px]
          font-semibold
          text-[#514B5E]
          shadow-lg
          backdrop-blur-sm
          sm:flex
        "
      >
        <span
          className="
            flex h-5 w-5
            items-center justify-center
            rounded-full
            bg-purple-100
            text-purple-600
          "
        >
          ✓
        </span>
        Benefits included
      </div>

      {/* Floating badge - bottom */}
      <div
        className="
          absolute
          bottom-3 left-0
          z-20
          hidden
          items-center gap-1.5
          rounded-xl
          border border-white/70
          bg-white/90
          px-2.5 py-2
          text-[10px]
          font-semibold
          text-[#514B5E]
          shadow-lg
          backdrop-blur-sm
          sm:flex
        "
      >
        <span className="h-2 w-2 rounded-full bg-emerald-500" />
        Ready to choose
      </div>
    </div>
  </div>
</div>

      <div className="mt-4 pb-4" aria-live="polite">
        {loading && <PlansGrid loading skeletonCount={8} />}

        {!loading && error && (
          <div
            role="alert"
            className="flex flex-col items-center justify-center py-16 gap-3 text-center"
          >
            <p className="text-base font-semibold text-red-600">
              Couldn't load plans right now.
            </p>
            <p className="text-sm text-[#8A8798] max-w-sm">{error}</p>
            <button
              type="button"
              onClick={refetch}
              className="mt-1 px-5 py-2 rounded-xl bg-[#14131A] text-white text-sm font-semibold hover:bg-black transition-colors"
            >
              Try again
            </button>
          </div>
        )}

        {!loading && !error && safePlans.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-2 text-center">
            <div className="h-12 w-12 rounded-full bg-[#F7F5FB] flex items-center justify-center mb-1">
              <Inbox size={20} className="text-[#C9BEE8]" strokeWidth={1.75} />
            </div>
            <p className="text-base font-semibold text-[#14131A]">
              No plans available yet
            </p>
            <p className="text-sm text-[#8A8798]">
              Check back soon, or{" "}
              <button
                type="button"
                onClick={onBrowseBenefits}
                className="font-semibold text-[#6D28D9] hover:underline"
              >
                build your own plan
              </button>
              .
            </p>
          </div>
        )}

        {!loading && !error && safePlans.length > 0 && (
          <PlansGrid
            plans={safePlans}
            selectedId={selectedId}
            activeIndex={activeIndex}
            onSelect={handleSelectPlan}
            onCardClick={handleCardClick}
          />
        )}
      </div>

      
      {activePlan && isActive && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#e6e2ef] border-t border-[#E8E5F0] shadow-[0_-4px_20px_rgba(20,19,26,0.06)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs font-medium text-[#8A8798]">
                Selected plan
              </p>
              <p className="text-sm font-bold text-[#14131A] truncate">
                {activePlan?.name ?? "—"}
              </p>
            </div>
            <button
              type="button"
              onClick={handleBuyActivePlan}
              className="shrink-0 px-5 py-2.5 rounded-xl bg-[#4C1D95] text-white text-sm font-semibold hover:bg-[#3B0764] active:scale-[0.99] transition-all duration-150 whitespace-nowrap"
            >
              Buy this plan
            </button>
          </div>
        </div>
      )}

      <PlanConfigModal
        plan={selectedPlan}
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
      />
    </section>
  );
};

export default ExistingPlansSection;
